import React, { useState, useEffect, useRef } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import MainWrapper from "../layouts/MainWrapper";
import CardWrapper from "../layouts/CardWrapper";
import Image from "react-bootstrap/Image";
import PageTitle from "../components/PageTitle";
import CustomBreadcrumb from "../components/CustomBreadcrumb";
import Dropdown from "react-bootstrap/Dropdown";
import Form from "react-bootstrap/Form";
import Tabs from "../components/Tabs/Tabs";
import { getSupportSessions, getInboxList } from "../services/supportService";
import { useAlert } from "../contexts/AlertContext";
import { listExpertUsers } from "../services/userService";
import scrollIntoView from "scroll-into-view-if-needed";
import eventBus from "../utils/eventBus";
import { authAtom } from "../states/auth";
import { useAtom } from "jotai";
import moment from "moment";

const formatDate = (date) => {
  if (!date) return "From - To Date";
  return moment(date).format("YYYY-MM-DD");
};

const getTodayRange = () => {
  return {
    from: moment().startOf("day").toDate(),
    to: moment().endOf("day").toDate(),
  };
};

const getThisWeekRange = () => {
  return {
    from: moment().startOf("isoWeek").toDate(),
    to: moment().endOf("isoWeek").toDate(),
  };
};

const getThisMonthRange = () => {
  return {
    from: moment().startOf("month").toDate(),
    to: moment().endOf("month").toDate(),
  };
};

const isSameDate = (d1, d2) => {
  if (!d1 || !d2) return false;
  return moment(d1).isSame(d2, "day");
};

function evaluateSessionTiming(startDateUTC, endDateUTC, isChat) {
  if (isChat === true) {
    return {
      isFutureSession: false,
      isPastSession: false,
      isSessionInWhile: false,
      isCurrentSession: false,
    };
  }

  const now = moment();
  const startDate = moment(startDateUTC);
  const endDate = moment(endDateUTC);

  const isFutureSession = startDate.isAfter(now);
  const isPastSession = endDate.isBefore(now);
  const isCurrentSession = now.isBetween(startDate, endDate, null, "[]");

  const diffInMillis = startDate.diff(now);
  // 10 minutes = 600000 ms
  const isSessionInWhile = diffInMillis > 0 && diffInMillis <= 600000;

  return {
    isFutureSession,
    isPastSession,
    isSessionInWhile,
    isCurrentSession,
  };
}

const getHourMinuteDiffString = (date1, date2) => {
  const start = moment(date1);
  const end = moment(date2);
  const duration = moment.duration(end.diff(start));

  const hours = Math.floor(duration.asHours());
  const minutes = duration.minutes();

  if (hours === 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
};

const SupportCenter = () => {
  const supportTimerRef = useRef(null);
  const inboxTimerRef = useRef(null);
  const heartbeatRef = useRef(null);

  const latestSupportDataRef = useRef([]);
  const latestInboxDataRef = useRef([]);

  const HEARTBEAT_MS = 1000;
  const DRIFT_TOLERANCE_MS = 1000;
  const EXTRA_BUFFER_MS = 1000;

  const initialRange = getThisWeekRange();
  const [range, setRange] = useState(initialRange);

  const [activeDateRange, setActiveDateRange] = useState(null);
  const [supportList, setSupportList] = useState(null);
  const [inboxList, setInboxList] = useState(null);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showStatusTypeDropdown, setStatusTypeDropdown] = useState(false);
  const [showUsersDropdown, setShowUsersDropdown] = useState(false);
  const dateRefs = useRef({});
  const hasScrolled = useRef(false);
  const [auth] = useAtom(authAtom);
  const [expertUsers, setExpertUsers] = useState([]);

  const [filter, setFilter] = useState({
    no_of_result: 0,
    page: 0,
    startDate: formatDate(initialRange?.from),
    endDate: formatDate(initialRange?.to),
    users: [],
    sessionType: "all",
  });

  const [inboxFilter, setInboxFilter] = useState({
    no_of_result: 0,
    page: 0,
    inboxSupportType: "all",
    inboxFilter: "all",
  });

  const { showAlert } = useAlert();

  const collectUpcomingTimes = (data, now) => {
    const times = [];
    if (!Array.isArray(data)) return times;

    // Use moment valueOf for comparisons
    const nowMs = moment(now).valueOf();

    for (const item of data) {
      if (item?.startTime) {
        const s = moment(item.startTime).valueOf();
        if (s > nowMs) times.push(s);
      }
      if (item?.endTime) {
        const e = moment(item.endTime).valueOf();
        if (e > nowMs) times.push(e);
      }
    }
    return times;
  };

  const scheduleNextRefresh = (data, setState, timerRef, label) => {
    try {
      if (!data || data.length === 0) {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
        return;
      }

      const now = Date.now();
      const upcoming = collectUpcomingTimes(data, now);

      if (upcoming.length === 0) {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
        return;
      }

      const nextTs = Math.min(...upcoming);
      let delay = Math.max(0, nextTs - now) + EXTRA_BUFFER_MS;

      if (timerRef.current) clearTimeout(timerRef.current);

      console.log(`Next ${label} refresh in ~${Math.round(delay / 1000)}s`);

      timerRef.current = setTimeout(() => {
        setState((prev) => [...(prev || [])]);
        scheduleNextRefresh(data, setState, timerRef, label);
      }, delay);
    } catch (err) {
      console.error(`scheduleNextRefresh error (${label}):`, err);
      if (timerRef.current) clearTimeout(timerRef.current);
    }
  };

  useEffect(() => {
    latestSupportDataRef.current = supportList || [];
  }, [supportList]);
  useEffect(() => {
    latestInboxDataRef.current = inboxList || [];
  }, [inboxList]);

  // Scroll to today logic
  useEffect(() => {
    if (!supportList || hasScrolled.current) return;

    const todayStr = moment().format("YYYY-MM-DD");
    const allDates = Object.keys(groupByDatesAndSort(supportList));

    const scrollToDate = allDates.includes(todayStr) ? todayStr : allDates[0];
    const el = dateRefs.current[scrollToDate];

    if (el) {
      scrollIntoView(el, {
        behavior: "smooth",
        scrollMode: "if-needed",
        block: "start",
        inline: "nearest",
      });
      hasScrolled.current = true;
    }
  }, [supportList]);

  // --- RANGE CHECKS USING MOMENT ---
  const isTodaySelected = () => {
    const today = moment();
    return isSameDate(range?.from, today) && isSameDate(range?.to, today);
  };

  const isThisWeekSelected = () => {
    const { from, to } = getThisWeekRange();
    return isSameDate(range?.from, from) && isSameDate(range?.to, to);
  };

  const isThisMonthSelected = () => {
    const { from, to } = getThisMonthRange();
    return isSameDate(range?.from, from) && isSameDate(range?.to, to);
  };

  const isToday = (from, to) => {
    if (!from || !to) return false;
    const today = moment();
    return moment(from).isSame(today, "day") && moment(to).isSame(today, "day");
  };

  const isThisWeek = (from, to) => {
    if (!from || !to) return false;
    const startOfWeek = moment().startOf("isoWeek");
    const endOfWeek = moment().endOf("isoWeek");
    return (
      moment(from).isSame(startOfWeek, "day") &&
      moment(to).isSame(endOfWeek, "day")
    );
  };

  const isThisMonth = (from, to) => {
    if (!from || !to) return false;
    const startOfMonth = moment().startOf("month");
    const endOfMonth = moment().endOf("month");
    return (
      moment(from).isSame(startOfMonth, "day") &&
      moment(to).isSame(endOfMonth, "day")
    );
  };

  useEffect(() => {
    setSupportList(null);
    setFilter((prev) => ({
      ...prev,
      startDate: formatDate(range?.from),
      endDate: formatDate(range?.to),
    }));

    if (isToday(range?.from, range?.to)) {
      setActiveDateRange("thisWeek");
    } else if (isThisMonth(range?.from, range?.to)) {
      setActiveDateRange("thisMonth");
    } else {
      setActiveDateRange(null);
    }
  }, [range]);

  const identifyUser = (data) => {
    let userMeta = { label: "", image: "" };
    const otherUser = data.createdBy.createdBy.id ?? data.createdBy.createdBy;
    if (data.createdBy.roleId == 3) {
      if (data.createdBy.createdBy === data.admin.id) {
        userMeta = {
          label: "Expert",
          image: "/images/user-circle-check-green.svg",
        };
      } else if (otherUser !== auth.user.id) {
        userMeta = {
          label: "Contact",
          image: "/images/user-circle-gear-blue.svg",
        };
      } else {
        userMeta = {
          label: "External Expert",
          image: "/images/user-circle-dashed-red.svg",
        };
      }
    } else if (data.createdBy.roleId == 5) {
      if (data.createdBy.createdBy === data.createdBy.id) {
        userMeta = {
          label: "Contact",
          image: "/images/user-circle-gear-blue.svg",
        };
      } else {
        userMeta = { label: "User", image: "/images/user-circle-yellow.svg" };
      }
    }
    return userMeta;
  };

  // --- GROUPING LOGIC WITH MOMENT (Fixes the Next Day / Timezone Issue) ---
  const groupByDatesAndSort = (data, showAllDates = true) => {
    const grouped = {};

    // Step 1: Initialize with all dates in the selected range using Moment
    let current = moment(filter?.startDate, "YYYY-MM-DD");
    const end = moment(filter?.endDate, "YYYY-MM-DD");

    if (showAllDates) {
      // Loop while current <= end
      while (current.isSameOrBefore(end, "day")) {
        const dateKey = current.format("YYYY-MM-DD");
        grouped[dateKey] = [];
        current.add(1, "days");
      }
    }

    // Step 2: Fill in sessions
    data?.forEach((item) => {
      // Convert UTC -> Local Moment -> YYYY-MM-DD Key
      const itemTime = moment(item.startTime || item.createdAt);
      const dateKey = itemTime.format("YYYY-MM-DD");

      if (grouped[dateKey]) {
        grouped[dateKey].push(item);
      } else if (!showAllDates) {
        // For inbox (where we might not show empty dates), create key on fly
        grouped[dateKey] = [item];
      }
    });
    return grouped;
  };

  const changeInboxFilterCriteriaByName = (name, value) => {
    inboxFilter[name] = value;
    setInboxFilter({ ...inboxFilter });
  };

  const changeFilterCriteriaByName = (name, value) => {
    filter[name] = value;
    setSupportList(null);
    setFilter({ ...filter });
  };

  const getInboxData = async () => {
    try {
      const data = await getInboxList(inboxFilter);
      setInboxList(data?.data);
      if (data?.data) {
        scheduleNextRefresh(data.data, setInboxList, inboxTimerRef, "inbox");
      } else {
        scheduleNextRefresh([], setInboxList, inboxTimerRef, "inbox");
      }
    } catch (error) {
      console.error("Error fetching inbox:", error);
      showAlert({
        title: "Error",
        message: error?.response?.data?.message ?? "something went wrong",
        variant: "danger",
      });
    }
  };

  const handleNewConversation = (data) => {
    setInboxList((prev) => {
      const exists = prev?.some((item) => item.id === data.id);
      const next = exists
        ? prev.map((item) => (item.id === data.id ? data : item))
        : [data, ...(prev || [])];
      scheduleNextRefresh(next, setInboxList, inboxTimerRef, "inbox");
      return next;
    });
  };

  const handleEndConversation = (data) => {
    setInboxList((prev) => {
      const next = (prev || []).filter((item) => item.id !== data.id);
      scheduleNextRefresh(next, setInboxList, inboxTimerRef, "inbox");
      return next;
    });
    setSupportList((prev) => {
      const next = (prev || []).filter((item) => item.id !== data.id);
      scheduleNextRefresh(next, setSupportList, supportTimerRef, "support");
      return next;
    });
  };

  const handleUpdateConversation = (data) => {
    if (data.conversationStatus === "Pending") {
      setInboxList((prev) => {
        const exists = prev?.some((item) => item.id === data.id);
        const next = exists
          ? prev.map((item) => (item.id === data.id ? data : item))
          : [data, ...(prev || [])];
        scheduleNextRefresh(next, setInboxList, inboxTimerRef, "inbox");
        return next;
      });
      setSupportList((prev) => {
        const next = (prev || []).filter((item) => item.id !== data.id);
        scheduleNextRefresh(next, setSupportList, supportTimerRef, "support");
        return next;
      });
    } else {
      setInboxList((prev) => {
        const next = (prev || []).filter((item) => item.id !== data.id);
        scheduleNextRefresh(next, setInboxList, inboxTimerRef, "inbox");
        return next;
      });
      setSupportList((prev) => {
        const exists = prev?.some((item) => item.id === data.id);
        const next = exists
          ? prev.map((item) => (item.id === data.id ? data : item))
          : [data, ...(prev || [])];
        scheduleNextRefresh(next, setSupportList, supportTimerRef, "support");
        return next;
      });
    }
  };

  const handleAcceptConversationConversation = (data) => {
    setInboxList((prev) => {
      const next = (prev || []).filter((item) => item.id !== data.id);
      scheduleNextRefresh(next, setInboxList, inboxTimerRef, "inbox");
      return next;
    });
    setSupportList((prev) => {
      const exists = prev?.some((item) => item.id === data.id);
      const next = exists
        ? prev.map((item) => (item.id === data.id ? data : item))
        : [data, ...(prev || [])];
      scheduleNextRefresh(next, setSupportList, supportTimerRef, "support");
      return next;
    });
  };

  useEffect(() => {
    let last = Date.now();
    heartbeatRef.current = setInterval(() => {
      const now = Date.now();
      const drift = Math.abs(now - last - HEARTBEAT_MS);
      if (drift > DRIFT_TOLERANCE_MS) {
        console.log("⏰ System clock adjusted — rescheduling timers");
        scheduleNextRefresh(
          latestSupportDataRef.current,
          setSupportList,
          supportTimerRef,
          "support",
        );
        scheduleNextRefresh(
          latestInboxDataRef.current,
          setInboxList,
          inboxTimerRef,
          "inbox",
        );
      }
      last = now;
    }, HEARTBEAT_MS);

    eventBus.on("ACCEPT_REQUEST", handleAcceptConversationConversation);
    eventBus.on("NEW_SUPPORT_REQUEST", handleNewConversation);
    eventBus.on("END_SUPPORT_REQUEST", handleEndConversation);
    eventBus.on("RESCHEDULED_UPDATE_EVENT", handleUpdateConversation);

    return () => {
      if (supportTimerRef.current) clearTimeout(supportTimerRef.current);
      if (inboxTimerRef.current) clearTimeout(inboxTimerRef.current);
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      eventBus.off("ACCEPT_REQUEST", handleAcceptConversationConversation);
      eventBus.off("NEW_SUPPORT_REQUEST", handleNewConversation);
      eventBus.off("END_SUPPORT_REQUEST", handleEndConversation);
      eventBus.on("RESCHEDULED_UPDATE_EVENT", handleUpdateConversation);
    };
  }, []);

  useEffect(() => {
    getInboxData();
  }, [inboxFilter]);

  useEffect(() => {
    const getSupportList = async () => {
      try {
        const data = await getSupportSessions(filter);
        setSupportList(data?.data);
        if (data?.data) {
          scheduleNextRefresh(
            data.data,
            setSupportList,
            supportTimerRef,
            "support",
          );
        } else {
          scheduleNextRefresh([], setSupportList, supportTimerRef, "support");
        }
      } catch (error) {
        console.error("Error fetching invitees:", error);
        showAlert({
          title: "Contact Access Notification",
          message: error?.response?.data?.message ?? "something went wrong",
          variant: "danger",
        });
      }
    };

    getSupportList();
  }, [filter]);

  useEffect(() => {
    const loadExpertUsers = async () => {
      try {
        const response = await listExpertUsers({ userTypeFilter: 0 });
        const usersData = response?.data || [];
        setExpertUsers(usersData);
        const allUserIds = usersData.map((user) => user.id);
        setFilter((prevFilter) => ({ ...prevFilter, users: allUserIds }));
      } catch (error) {
        console.error("Error loading expert users:", error);
      }
    };
    loadExpertUsers();
  }, []);

  const BeadcrumbItems = [{ label: "Sessions", active: true }];
  const tabItem = { All: "all", Chat: "chats", Video: "video" };
  const [searchTerm, setSearchTerm] = useState("");

  const filteredExperts = expertUsers?.filter((expert) => {
    const fullName = (expert.firstName + " " + expert.lastName).toLowerCase();
    return fullName.includes(searchTerm.toLowerCase());
  });

  const selectedUsers = filter?.users || [];
  const selectedLabel = (() => {
    if (selectedUsers.length === expertUsers.length && expertUsers.length > 0)
      return "All Team Members";
    if (selectedUsers.length === 1) {
      const user = expertUsers.find((u) => u.id === selectedUsers[0]);
      return user ? `${user.firstName} ${user.lastName}` : "1 Selected";
    }
    if (selectedUsers.length > 1) return `${selectedUsers.length} Selected`;
    return "All Team Members";
  })();

  const sessionLabelMap = {
    all: "Sessions",
    client: "Received",
    internal_user: "Internal",
    external_expert: "Requested",
  };

  return (
    <>
      <div className="mb-4">
        <CustomBreadcrumb items={BeadcrumbItems} />
        <PageTitle title="Sessions" />
      </div>
      <MainWrapper className="row w-auto gy-4">
        <Col md={8} xs={12} className="order-2 order-md-1">
          <CardWrapper className="p-0 h-100">
            <div className="p-3 border-bottom">
              <Row className="align-items-center gy-2">
                <Col lg="12">
                  <div className="d-flex align-items-center gap-4">
                    <h2 className="fs-5 fw-semibold text-gray-800 mb-0">
                      Calendar
                    </h2>
                  </div>
                </Col>
                <Col lg="5">
                  <ul className="d-flex align-items-center list-unstyled mb-0">
                    <li className="me-4">
                      <button
                        className={`btn-reset fs-14 ${isTodaySelected() ? "text-blue" : "text-gray-700"}`}
                        onClick={() => setRange(getTodayRange())}
                      >
                        Today
                      </button>
                    </li>
                    <li className="me-4">
                      <button
                        className={`btn-reset fs-14 ${isThisWeekSelected() ? "text-blue" : "text-gray-700"}`}
                        onClick={() => setRange(getThisWeekRange())}
                      >
                        This Week
                      </button>
                    </li>
                    <li className="me-4">
                      <button
                        className={`btn-reset fs-14 ${isThisMonthSelected() ? "text-blue" : "text-gray-700"}`}
                        onClick={() => setRange(getThisMonthRange())}
                      >
                        This Month
                      </button>
                    </li>
                  </ul>
                </Col>
                <Col lg="7">
                  <div className="d-flex flex-wrap align-items-center justify-content-end gap-2">
                    <Dropdown align="end">
                      <Dropdown.Toggle
                        as="button"
                        className="filter-dropdown-button d-flex align-items-center gap-2"
                      >
                        {range?.from && range?.to
                          ? `${formatDate(range?.from)} - ${formatDate(range.to)}`
                          : "From - To Date"}
                        <Image src="/images/calendar-dots.svg" alt="Calendar" />
                      </Dropdown.Toggle>
                      <Dropdown.Menu className="calendar-dropdown-menu dropdown-menu-end">
                        <DayPicker
                          mode="range"
                          selected={range}
                          onSelect={setRange}
                        />
                      </Dropdown.Menu>
                    </Dropdown>

                    {/* Users Dropdown (Logic Unchanged, just JSX layout) */}
                    <Dropdown
                      show={showUsersDropdown}
                      onToggle={setShowUsersDropdown}
                    >
                      <Dropdown.Toggle
                        variant="light"
                        className="filter-dropdown-button dropdown-toggle-arrow"
                        align="end"
                      >
                        {selectedLabel}
                      </Dropdown.Toggle>
                      <Dropdown.Menu
                        className="filter-dropdown-menu user-dropdown-menu dropdown-menu-end p-0"
                        align="end"
                      >
                        <div className="filter-dropdown-menu-list p-2 border-bottom">
                          <input
                            className="form-control form-control-search"
                            placeholder="Search here..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </div>
                        <div className="px-3 pt-2 border-bottom">
                          <Form.Check
                            className="checkbox-primary mb-2"
                            type="checkbox"
                            label="Select All"
                            checked={
                              filter?.users?.length === expertUsers?.length
                            }
                            onChange={(e) => {
                              const newUsers = e.target.checked
                                ? expertUsers.map((u) => u.id)
                                : [];
                              setSupportList(null);
                              setFilter({ ...filter, users: newUsers });
                            }}
                          />
                        </div>
                        <div
                          className="filter-dropdown-menu-list px-3 py-2 border-bottom"
                          style={{ maxHeight: "250px", overflowY: "auto" }}
                        >
                          {filteredExperts?.map((expert) => (
                            <Form.Check
                              key={expert.id}
                              className="checkbox-primary mb-2"
                              type="checkbox"
                              label={expert.firstName + " " + expert.lastName}
                              checked={filter.users.includes(expert.id)}
                              onChange={(e) => {
                                const updatedUsers = e.target.checked
                                  ? [...filter?.users, expert.id]
                                  : filter?.users?.filter(
                                      (id) => id !== expert.id,
                                    );
                                setSupportList(null);
                                setFilter({ ...filter, users: updatedUsers });
                              }}
                            />
                          ))}
                        </div>
                        <div className="px-3 py-2 d-flex justify-content-between">
                          <button
                            className="btn btn-sm btn-reset text-blue"
                            onClick={() => {
                              setShowUsersDropdown(false);
                              setFilter({
                                ...filter,
                                users: expertUsers.map((u) => u.id),
                              });
                              setSearchTerm("");
                            }}
                          >
                            Reset
                          </button>
                          <button
                            onClick={() => {
                              setShowUsersDropdown(false);
                              setSearchTerm("");
                            }}
                            className="btn btn-sm btn-blue"
                          >
                            Okay
                          </button>
                        </div>
                      </Dropdown.Menu>
                    </Dropdown>
                    {/* Session Type Dropdown */}
                    <Dropdown
                      show={showStatusTypeDropdown}
                      onToggle={setStatusTypeDropdown}
                    >
                      <Dropdown.Toggle
                        variant="light"
                        className="filter-dropdown-button dropdown-toggle-arrow"
                        align="end"
                      >
                        {sessionLabelMap[filter.sessionType] || "Sessions"}
                      </Dropdown.Toggle>
                      <Dropdown.Menu
                        className="filter-dropdown-menu dropdown-menu-end p-0"
                        align="end"
                      >
                        {/* ... Options kept as is ... */}
                        <div className="px-2 pt-2 border-bottom">
                          <Form.Check
                            className="radio-primary mb-2"
                            type="radio"
                            label="Select All"
                            checked={filter.sessionType === "all"}
                            onChange={() =>
                              changeFilterCriteriaByName("sessionType", "all")
                            }
                          />
                        </div>
                        <div className="px-3 py-2 border-bottom">
                          <Form.Check
                            className="radio-primary mb-1"
                            type="radio"
                            label="Received"
                            checked={filter.sessionType === "client"}
                            onChange={() =>
                              changeFilterCriteriaByName(
                                "sessionType",
                                "client",
                              )
                            }
                          />
                          <Form.Check
                            className="radio-primary mb-1"
                            type="radio"
                            label="Internal"
                            checked={filter.sessionType === "internal_user"}
                            onChange={() =>
                              changeFilterCriteriaByName(
                                "sessionType",
                                "internal_user",
                              )
                            }
                          />
                          <Form.Check
                            className="radio-primary mb-1"
                            type="radio"
                            label="Requested"
                            checked={filter.sessionType === "external_expert"}
                            onChange={() =>
                              changeFilterCriteriaByName(
                                "sessionType",
                                "external_expert",
                              )
                            }
                          />
                        </div>
                        <div className="p-2 d-flex justify-content-between">
                          <button
                            onClick={() => {
                              setStatusTypeDropdown(false);
                              changeFilterCriteriaByName("sessionType", "all");
                            }}
                            className="btn btn-xm btn-reset text-blue"
                          >
                            Reset
                          </button>
                          <button
                            onClick={() => setStatusTypeDropdown(false)}
                            className="btn btn-xm btn-blue"
                          >
                            Okay
                          </button>
                        </div>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </Col>
              </Row>
            </div>

            <div className="full-box-2 p-3">
              {supportList && Object.keys(supportList).length === 0 && (
                <div className="session-item bg-white cursor-auto border-grey-300 d-flex justify-content-center mb-3">
                  <p className="fs-16 fw-500 text-gray-400">
                    No Events Available
                  </p>
                </div>
              )}

              {supportList &&
                Object.entries(groupByDatesAndSort(supportList))
                  .sort(([a], [b]) => moment(a).diff(moment(b))) // Sort by date using Moment
                  .map(
                    ([date, sessions]) =>
                      sessions.length > 0 && (
                        <div
                          className="session-item-group"
                          key={date}
                          ref={(el) => (dateRefs.current[date] = el)}
                        >
                          {/* HEADER: Display Date in Local Time */}
                          <p className="text-gray-500 fs-15 fw-semibold mb-2">
                            {moment(date).format("dddd, MMM D")}
                          </p>

                          {sessions
                            .sort((a, b) =>
                              moment(a.startTime).diff(moment(b.startTime)),
                            )
                            .map((session) => {
                              const userMeta = identifyUser(session);
                              const chatType =
                                session?.conversationType?.includes("chat");
                              const sessionTimeStatus = evaluateSessionTiming(
                                session.startTime,
                                session.endTime,
                                chatType,
                              );

                              const isCreator =
                                session?.createdBy?.createdBy === auth.user.id;
                              const isSupport = [
                                "support call",
                                "support chat",
                              ].includes(session?.conversationType);
                              console.log("isCreator", isCreator, isSupport);
                              const anotherCompanyMember =
                                session?.conversationMembers?.find(
                                  (e) =>
                                    e.user?.companyName &&
                                    e.user?.createdBy != auth.user.id,
                                );
                             
                              const targetUser =
                                isCreator &&
                                isSupport &&
                                anotherCompanyMember?.user
                                  ? {
                                      ...anotherCompanyMember.user,
                                      id: anotherCompanyMember.user_id,
                                    }
                                  : session.createdBy;
                              const myExpertUser =
                                session?.conversationMembers?.find(
                                  (e) => e.user_id !== targetUser?.id,
                                );
                              
                              return (
                                <div
                                  className="session-item cursor-auto"
                                  key={session.id}
                                >
                                  <div className="session-info">
                                    <Image
                                      src={
                                        session.conversationType.includes(
                                          "chat",
                                        )
                                          ? "/images/chat-circle-text.svg"
                                          : "/images/video-camera.svg"
                                      }
                                      width={32}
                                      height={32}
                                      alt="Icon"
                                    />
                                    <p className="text-gray-700 fs-12 fw-semibold mt-2 mb-0">
                                      {/* TIME: Display Time in Local Time */}
                                      {moment(
                                        session.startTime || session.createdAt,
                                      ).format("h:mm A")}

                                      {session.startTime && session.endTime && (
                                        <span className="d-inline-block fw-normal">
                                          (
                                          {getHourMinuteDiffString(
                                            session.startTime,
                                            session.endTime,
                                          )}
                                          )
                                        </span>
                                      )}
                                    </p>
                                  </div>
                                  <div className="session-details col">
                                    <div className="d-flex flex-wrap gap-1 align-items-center justify-content-between">
                                      <h3 className="fs-18 fw-bold mb-1">
                                        {session.createdBy.companyName ||
                                          "OEM Company Name"}
                                      </h3>
                                      {(session.conversationType ===
                                        "support chat" ||
                                        sessionTimeStatus?.isCurrentSession) && (
                                        <span className="in-session">
                                          In Session
                                        </span>
                                      )}
                                    </div>
                                    <p className="fs-14 fw-medium text-gray-600 mb-0">
                                      {session?.expertise?.name || ""}
                                    </p>
                                    <ul className="d-md-flex align-items-md-center gap-2 list-unstyled mt-2 mb-0">
                                      <li className="d-flex align-items-center gap-1">
                                        <Image
                                          src={
                                            userMeta?.image ||
                                            "/images/user-circle-gear-blue.svg"
                                          }
                                          width={20}
                                          height={20}
                                          alt="User"
                                        />
                                        <span className="fs-14 text-gray-500">
                                          {userMeta.label}:{" "}
                                          {session?.createdBy?.createdBy !==
                                            auth.user.id}{" "}
                                          {targetUser?.firstName}{" "}
                                          {targetUser?.lastName}
                                        </span>
                                      </li>
                                      <li className="dot-separator"></li>
                                      <li className="d-flex align-items-center gap-1">
                                        <Image
                                          src="/images/user-circle-check-green.svg"
                                          width={20}
                                          height={20}
                                          alt="User"
                                        />

                                        <span className="fs-14 text-gray-500">
                                          Expert:{" "}
                                          {myExpertUser
                                            ? `${myExpertUser.user.firstName} ${myExpertUser.user.lastName}`
                                            : "N/A"}
                                        </span>
                                      </li>
                                    </ul>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      ),
                  )}
            </div>
          </CardWrapper>
        </Col>

        {/* INBOX SECTION */}
        <Col md={4} xs={12} className="order-1 order-md-2">
          <CardWrapper className="bg-gray-200 p-0 h-100">
            <div className="p-3 border-bottom border-grey-300">
              {/* ... Inbox Header ... */}
              <Row className="align-items-center gy-4 gy-xxl-0">
                <Col sm="3">
                  <h2 className="fs-5 fw-semibold text-gray-700 mb-0">Inbox</h2>
                </Col>
                <Col
                  sm="9"
                  className="d-flex align-items-center justify-content-end gap-2"
                >
                  <Tabs
                    tabItem={tabItem}
                    changeFunction={changeInboxFilterCriteriaByName}
                    changeValue={"inboxSupportType"}
                    className="md"
                  />
                  <Dropdown
                    show={showStatusDropdown}
                    onToggle={setShowStatusDropdown}
                  >
                    <Dropdown.Toggle
                      variant="light"
                      className="filter-btn-icon dropdown-toggle-none border-0"
                      align="end"
                    >
                      <Image src="/images/funnel-line.svg" alt="Filter" />
                    </Dropdown.Toggle>
                    <Dropdown.Menu
                      className="filter-dropdown-menu dropdown-menu-end p-0"
                      align="end"
                    >
                      <div className="px-3 pt-2 border-bottom">
                        <Form.Check
                          className="radio-primary mb-2"
                          type="radio"
                          label="Select All"
                          name="inboxFilter"
                          onChange={() =>
                            changeInboxFilterCriteriaByName(
                              "inboxFilter",
                              "all",
                            )
                          }
                          checked={inboxFilter.inboxFilter === "all"}
                        />
                      </div>
                      <div className="px-3 py-2 border-bottom">
                        <Form.Check
                          className="radio-primary mb-1"
                          type="radio"
                          label="New"
                          onChange={() =>
                            changeInboxFilterCriteriaByName(
                              "inboxFilter",
                              "new",
                            )
                          }
                          checked={inboxFilter.inboxFilter === "new"}
                        />
                        <Form.Check
                          className="radio-primary mb-1"
                          type="radio"
                          label="Expired"
                          onChange={() =>
                            changeInboxFilterCriteriaByName(
                              "inboxFilter",
                              "expired",
                            )
                          }
                          checked={inboxFilter.inboxFilter === "expired"}
                        />
                      </div>
                      <div className="p-2 d-flex justify-content-between">
                        <button
                          onClick={() => {
                            setShowStatusDropdown(false);
                            changeInboxFilterCriteriaByName(
                              "inboxFilter",
                              "all",
                            );
                          }}
                          className="btn btn-xm text-blue"
                        >
                          Reset
                        </button>
                        <button
                          onClick={() => setShowStatusDropdown(false)}
                          className="btn btn-xm btn-blue"
                        >
                          Okay
                        </button>
                      </div>
                    </Dropdown.Menu>
                  </Dropdown>
                </Col>
              </Row>
            </div>
            <div className="inbox-session full-box p-3">
              {inboxList && Object.keys(inboxList).length === 0 && (
                <div className="session-item bg-white cursor-auto border-grey-300 d-flex justify-content-center mb-3">
                  <p className="fs-16 fw-500 text-gray-400">
                    No Events Available
                  </p>
                </div>
              )}

              {inboxList &&
                Object.entries(groupByDatesAndSort(inboxList, false))
                  .sort(([a], [b]) => moment(a).diff(moment(b)))
                  .map(
                    ([date, sessions]) =>
                      sessions.length > 0 && (
                        <div className="session-item-group" key={date}>
                          {/* INBOX HEADER: Local Date */}
                          <p className="text-gray-500 fs-15 fw-semibold mb-2">
                            {moment(date).format("dddd, MMM D")}
                          </p>

                          {sessions
                            .sort((a, b) =>
                              moment(a.startTime).diff(moment(b.startTime)),
                            )
                            .map((session) => {
                              const userMeta = identifyUser(session);
                              return (
                                <div
                                  className="session-item radius-12"
                                  key={session?.id}
                                >
                                  <div className="session-info">
                                    <Image
                                      src={
                                        session.conversationType.includes(
                                          "chat",
                                        )
                                          ? "/images/chat-circle-text.svg"
                                          : "/images/video-camera.svg"
                                      }
                                      width={32}
                                      height={32}
                                      alt="Icon"
                                    />
                                    <p className="text-gray-700 fs-12 fw-semibold mt-2 mb-0">
                                      {/* INBOX TIME: Local Time */}
                                      {moment(
                                        session.startTime || session.createdAt,
                                      ).format("h:mm A")}

                                      {session.startTime && session.endTime && (
                                        <span className="d-inline-block fw-normal">
                                          (
                                          {getHourMinuteDiffString(
                                            session.startTime,
                                            session.endTime,
                                          )}
                                          )
                                        </span>
                                      )}
                                    </p>
                                  </div>
                                  <div className="session-details col">
                                    <div className="d-flex flex-wrap gap-1 align-items-center justify-content-between">
                                      <h3 className="fs-14 mb-1">
                                        {session?.createdBy?.companyName}
                                      </h3>
                                      {/* EXPIRED LOGIC USING MOMENT */}
                                      {session?.startTime &&
                                      moment(session.startTime).isBefore(
                                        moment(),
                                      ) ? (
                                        <span className="badge expired">
                                          Expired
                                        </span>
                                      ) : (
                                        <span className="badge new">New</span>
                                      )}
                                    </div>
                                    <p className="fs-12 fw-500 text-gray-600 mb-0">
                                      {session?.expertise?.name}
                                    </p>
                                    <ul className="d-flex align-items-center gap-2 list-unstyled mt-2 mb-0">
                                      <li className="d-flex align-items-center gap-1">
                                        <Image
                                          src={
                                            userMeta?.image ||
                                            "/images/user-circle-gear-blue.svg"
                                          }
                                          width={20}
                                          height={20}
                                          alt="Icon"
                                        />
                                        <span className="fs-12 text-gray-500">
                                          {userMeta.label}:{" "}
                                          {session?.createdBy?.firstName}{" "}
                                          {session?.createdBy?.lastName}
                                        </span>
                                      </li>
                                    </ul>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      ),
                  )}
            </div>
          </CardWrapper>
        </Col>
      </MainWrapper>
    </>
  );
};

export default SupportCenter;
